import { t } from "@lingui/macro";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useEffect, useState, useRef } from "react";

interface AiQuestionGeneratorProps {
  open: boolean;
  onClose: () => void;
  selfAssessmentId?: string;
  studentId: string;
  onQuestionsAdded: () => void;
  onAlert: (message: string, severity: "success" | "error" | "warning" | "info") => void;
}

const AiQuestionGenerator: React.FC<AiQuestionGeneratorProps> = ({
  open,
  onClose,
  selfAssessmentId,
  studentId,
  onQuestionsAdded,
  onAlert,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    type: "",
    category: "",
    skill: [] as string[],
    numberOfQuestions: 1,
    level: "",
    tags: [] as string[],
    additionalContext: "",
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<any>(null);
  const [generatedQuestions, setGeneratedQuestions] = useState<any[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [availableSkills, setAvailableSkills] = useState<string[]>([]);
  const [customCategoryOptions, setCustomCategoryOptions] = useState<Record<string, string[]>>({});
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [customTagOptions, setCustomTagOptions] = useState<Record<string, string[]>>({});
  const [isAddingQuestions, setIsAddingQuestions] = useState(false);
  const [canAddQuestions, setCanAddQuestions] = useState(false);
  const [deletingQuestions, setDeletingQuestions] = useState<Set<string>>(new Set());
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [addSkillDialogOpen, setAddSkillDialogOpen] = useState(false);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const [newSkillInput, setNewSkillInput] = useState("");
  const [newTagInput, setNewTagInput] = useState("");
  const [isAddingNewSkill, setIsAddingNewSkill] = useState(false);
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);
  const [skillDropdownOpen, setSkillDropdownOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [skillError, setSkillError] = useState(false);
  const [levelError, setLevelError] = useState(false);
  const [tagError, setTagError] = useState(false);
  const [originalQuestionCount, setOriginalQuestionCount] = useState(0);
  const [regenerationJobId, setRegenerationJobId] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasShownRegenerationSuccess, setHasShownRegenerationSuccess] = useState(false);
  const skillScrollPositionRef = useRef(0);

  const questionTypes = [
    { value: "short-answer", label: t`Short Answer` },
    { value: "single-choice", label: t`Single Choice` },
    { value: "multiple-choice", label: t`Multiple Choice` },
  ];

  const levels = [
    { value: "beginner", label: t`Beginner` },
    { value: "intermediate", label: t`Intermediate` },
    { value: "advanced", label: t`Advanced` },
  ];

  const categories = [
    { value: "Technical", label: "Technical" },
    { value: "Aptitude", label: "Aptitude" },
    { value: "Logical Reasoning", label: "Logical Reasoning" },
    { value: "Verbal Ability", label: "Verbal Ability" },
    { value: "Database", label: "Database" },
    { value: "Programming", label: "Programming" },
    { value: "Cloud & DevOps", label: "Cloud & DevOps" },
    { value: "AI & Machine Learning", label: "AI & Machine Learning" },
  ];

  const categoryFieldConfig: Record<string, { label: string; options: string[] }> = {
    Technical: {
      label: "Technology / Skill",
      options: ["React", "Angular", "Node.js", "Java", "Python", "SQL", "AWS"],
    },
    Aptitude: {
      label: "Aptitude Topic",
      options: [
        "Number System", "Percentages", "Profit & Loss",
        "Time & Work", "Time, Speed & Distance", "Probability",
      ],
    },
    "Logical Reasoning": {
      label: "Reasoning Topic",
      options: ["Puzzles", "Seating Arrangement", "Blood Relations", "Coding-Decoding", "Syllogisms"],
    },
    "Verbal Ability": {
      label: "Verbal Topic",
      options: ["Reading Comprehension", "Grammar", "Vocabulary", "Synonyms", "Antonyms"],
    },
    Database: {
      label: "Database Technology",
      options: ["MySQL", "PostgreSQL", "MongoDB", "Oracle", "SQL Server"],
    },
    Programming: {
      label: "Programming Language",
      options: ["C", "C++", "Java", "Python", "JavaScript", "Go", "Rust"],
    },
    "Cloud & DevOps": {
      label: "Cloud Technology",
      options: ["AWS", "Azure", "GCP", "Docker", "Kubernetes", "Jenkins"],
    },
    "AI & Machine Learning": {
      label: "AI / ML Topic",
      options: ["Machine Learning", "Deep Learning", "NLP", "Computer Vision", "LLMs", "Prompt Engineering"],
    },
  };

  const selectedCategoryConfig = categoryFieldConfig[formData.category] || {
    label: "Technology / Skill",
    options: [],
  };
  const selectedCategoryCustomOptions = customCategoryOptions[formData.category] || [];
  const selectedCategoryOptions = [
    ...selectedCategoryConfig.options,
    ...selectedCategoryCustomOptions.filter(
      (customOption) =>
        !selectedCategoryConfig.options.some(
          (defaultOption) => defaultOption.toLowerCase() === customOption.toLowerCase()
        )
    ),
  ];

  const DEFAULT_SKILLS = categoryFieldConfig.Technical.options;

  const defaultTagsConfig: Record<string, string[]> = {
    Technical: ["Frontend", "Backend", "Full Stack", "API", "Security", "Testing", "Performance", "Architecture"],
    Aptitude: ["Arithmetic", "Quantitative", "Problem Solving", "Mental Math", "Data Interpretation", "Logical"],
    "Logical Reasoning": ["Puzzle", "Pattern", "Deduction", "Arrangement", "Syllogism", "Analytical"],
    "Verbal Ability": ["Comprehension", "Grammar", "Vocabulary", "Synonyms", "Antonyms", "Sentence Correction"],
    Database: ["SQL", "NoSQL", "Query Optimization", "Indexing", "Transactions", "Normalization"],
    Programming: ["DSA", "Algorithm", "Data Structure", "OOP", "Recursion", "Dynamic Programming"],
    "Cloud & DevOps": ["CI/CD", "Container", "Orchestration", "Infrastructure", "Networking", "Monitoring"],
    "AI & Machine Learning": ["Supervised", "Unsupervised", "Neural Network", "NLP", "Computer Vision", "Generative AI"],
  };

  const selectedCategoryDefaultTags = defaultTagsConfig[formData.category] || [];
  const selectedCategoryCustomTags = customTagOptions[formData.category] || [];
  const selectedCategoryAllTags = [
    ...selectedCategoryDefaultTags,
    ...selectedCategoryCustomTags.filter(
      (customTag) =>
        !selectedCategoryDefaultTags.some(
          (defaultTag) => defaultTag.toLowerCase() === customTag.toLowerCase()
        )
    ),
  ];

  // Fetch available skills — category-aware
  useEffect(() => {
    const fetchSkills = async () => {
      setSkillsLoading(true);
      try {
        if (formData.category !== "Technical") {
          setAvailableSkills(selectedCategoryOptions);
          return;
        }

        const response = await axios.get("/exam/api/question/skills/unique?context=quiz");
        const backendSkills = response.data.result || [];

        const allSkills = [...DEFAULT_SKILLS];
        backendSkills.forEach((skill: string) => {
          const isDuplicate = allSkills.some(
            (existingSkill) => existingSkill.toLowerCase() === skill.toLowerCase()
          );
          if (!isDuplicate) allSkills.push(skill);
        });
        selectedCategoryCustomOptions.forEach((skill) => {
          const isDuplicate = allSkills.some(
            (existingSkill) => existingSkill.toLowerCase() === skill.toLowerCase()
          );
          if (!isDuplicate) allSkills.push(skill);
        });

        setAvailableSkills(allSkills);
      } catch (error) {
        console.error("Error fetching skills:", error);
        setAvailableSkills(DEFAULT_SKILLS);
      } finally {
        setSkillsLoading(false);
      }
    };
    if (open && formData.category) {
      fetchSkills();
    } else if (open) {
      setAvailableSkills([]);
    }
  }, [open, formData.category, customCategoryOptions]);

  // Fetch available tags — category-aware
  useEffect(() => {
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        const response = await axios.get("/exam/api/question/tags/unique?context=quiz");
        const backendTags = response.data.result || [];

        const allTags = [...selectedCategoryAllTags];
        backendTags.forEach((tag: string) => {
          const isDuplicate = allTags.some(
            (existingTag) => existingTag.toLowerCase() === tag.toLowerCase()
          );
          if (!isDuplicate) allTags.push(tag);
        });

        setAvailableTags(allTags);
      } catch (error) {
        console.error("Error fetching tags:", error);
        setAvailableTags(selectedCategoryAllTags);
      } finally {
        setTagsLoading(false);
      }
    };
    if (open && formData.category) {
      fetchTags();
    } else if (open) {
      setAvailableTags([]);
    }
  }, [open, formData.category, customTagOptions]);

  // Helper function to ensure questions have proper structure
  const ensureQuestionStructure = (questions: any[]) => {
    return questions.map((q: any, i: number) => {
      // Ensure level is preserved from formData if not in question
      const level = q.level || formData.level || "intermediate";
      return {
        ...q,
        id: `ai-${Date.now()}-${i}`,
        level: level, // Ensure level is always present
      };
    });
  };

  useEffect(() => {
    if (!(regenerationJobId || jobId) || !(isRegenerating || isGenerating)) return;

    const interval = setInterval(async () => {
      try {
        const currentJobId = regenerationJobId || jobId;
        const res = await axios.get(`/api/integration/external-service/ai/status/${currentJobId}`);
        const status = res.data.data || res.data.result || res.data;
        setGenerationStatus(status);

        if (status.status === "completed") {
          if (isRegenerating && regenerationJobId) {
            // Only process once
            if (hasShownRegenerationSuccess) {
              clearInterval(interval);
              return;
            }

            try {
              // Merge regenerated questions
              await axios.post(`/api/integration/external-service/ai/merge/${jobId}/${regenerationJobId}`);

              // Fetch ALL merged questions from the ORIGINAL job
              const updatedRes = await axios.get(`/api/integration/external-service/ai/status/${jobId}`);
              const updatedStatus = updatedRes.data.data || updatedRes.data.result || updatedRes.data;

              // Ensure questions have proper structure with level
              const questions = ensureQuestionStructure(updatedStatus.questions || []);

              // Update state with ALL questions (original + regenerated)
              setGeneratedQuestions(questions);
              setOriginalQuestionCount(questions.length);
              setIsRegenerating(false);
              setRegenerationJobId(null);
              setCanAddQuestions(true);
              setHasShownRegenerationSuccess(true);

              onAlert(`Successfully regenerated questions! Total: ${questions.length}`, "success");
            } catch (mergeError) {
              console.error("Error merging questions:", mergeError);
              onAlert("Failed to merge regenerated questions", "error");
              setIsRegenerating(false);
              setCanAddQuestions(true);
            }
          } else {
            // Initial generation
            const questions = ensureQuestionStructure(status.questions || []);
            setGeneratedQuestions(questions);
            setOriginalQuestionCount(questions.length);
            setIsGenerating(false);
            setCanAddQuestions(true);
            onAlert(`Successfully generated ${questions.length} questions!`, "success");
          }
        } else if (status.status === "failed") {
          setIsRegenerating(false);
          setIsGenerating(false);
          setCanAddQuestions(false);
          onAlert(`Generation failed: ${status.error || "Unknown error"}`, "error");
        }
      } catch (error) {
        console.error("Error polling status:", error);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, regenerationJobId, isGenerating, isRegenerating, hasShownRegenerationSuccess, formData.level]);

  const handleCloseDrawer = () => {
    resetFormState();
    onClose();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      if (field === "category") {
        return { ...prev, category: value, skill: [], tags: [] };
      }
      return { ...prev, [field]: value };
    });
    if (field === "type") setTypeError(false);
    if (field === "category") {
      setCategoryError(false);
      setSkillError(false);
      setTagError(false);
    }
    if (field === "skill") setSkillError(false);
    if (field === "level") setLevelError(false);
    if (field === "tags") setTagError(false);
  };

  const toCamelCase = (str: string): string => {
    const trimmed = str.trim();
    if (!trimmed.includes(" ") && !trimmed.includes("-") && !trimmed.includes("_")) {
      return trimmed.toLowerCase();
    }
    return trimmed
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
  };

  const findExistingItem = (newItem: string, existingItems: string[]): string | null => {
    const normalizedNew = toCamelCase(newItem);
    return existingItems.find((item) => toCamelCase(item) === normalizedNew) || null;
  };

  const handleAddSkill = async (newSkill: string) => {
    const trimmedSkill = newSkill.trim();
    const category = formData.category;
    const fieldLabel = selectedCategoryConfig.label;

    if (!category) {
      onAlert("Please select a category first", "warning");
      return trimmedSkill;
    }

    const existingSkill = findExistingItem(trimmedSkill, availableSkills);
    if (existingSkill) {
      onAlert(`${fieldLabel} "${existingSkill}" already exists!`, "info");
      return existingSkill;
    }

    setCustomCategoryOptions((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), trimmedSkill],
    }));
    setAvailableSkills((prev) => [...prev, trimmedSkill]);
    onAlert(`New ${fieldLabel.toLowerCase()} "${trimmedSkill}" added successfully!`, "success");
    return trimmedSkill;
  };

  const handleAddTag = async (newTag: string) => {
    const trimmedTag = newTag.trim();
    const category = formData.category;

    if (!category) {
      onAlert("Please select a category first", "warning");
      return trimmedTag;
    }

    const existingTag = findExistingItem(trimmedTag, availableTags);
    if (existingTag) {
      onAlert(`Tag "${existingTag}" already exists!`, "info");
      return existingTag;
    }

    setCustomTagOptions((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), trimmedTag],
    }));
    setAvailableTags((prev) => [...prev, trimmedTag]);
    onAlert(`New tag "${trimmedTag}" added successfully!`, "success");
    return trimmedTag;
  };

  const handleAddNewSkillFromDialog = async () => {
    if (!newSkillInput.trim()) {
      onAlert(`Please enter ${selectedCategoryConfig.label.toLowerCase()}`, "warning");
      return;
    }
    setIsAddingNewSkill(true);
    try {
      const addedSkill = await handleAddSkill(newSkillInput.trim());
      const updatedSkills = formData.skill.some(
        (skill) => skill.toLowerCase() === addedSkill.toLowerCase()
      )
        ? formData.skill
        : [...formData.skill, addedSkill];
      handleInputChange("skill", updatedSkills);
      setNewSkillInput("");
      setAddSkillDialogOpen(false);
    } catch (error) {
      console.error("Error adding skill:", error);
    } finally {
      setIsAddingNewSkill(false);
    }
  };

  const handleAddNewTagFromDialog = async () => {
    if (!newTagInput.trim()) {
      onAlert("Please enter a tag name", "warning");
      return;
    }
    setIsAddingNewTag(true);
    try {
      const addedTag = await handleAddTag(newTagInput.trim());
      handleInputChange("tags", [...formData.tags, addedTag]);
      setNewTagInput("");
      setAddTagDialogOpen(false);
    } catch (error) {
      console.error("Error adding tag:", error);
    } finally {
      setIsAddingNewTag(false);
    }
  };

  const handleGenerateQuestions = async () => {
    const errors = {
      type: !formData.type,
      category: !formData.category,
      skill: formData.skill.length === 0,
      level: !formData.level,
      tags: formData.tags.length === 0,
    };
    setTypeError(errors.type);
    setCategoryError(errors.category);
    setSkillError(errors.skill);
    setLevelError(errors.level);
    setTagError(errors.tags);

    if (!formData.numberOfQuestions || formData.numberOfQuestions < 1) {
      onAlert("Number of questions must be at least 1", "error");
      return;
    }

    if (formData.numberOfQuestions > 25) {
      onAlert("Number of questions cannot exceed 25", "error");
      return;
    }

    if (Object.values(errors).some(Boolean)) return;

    setIsGenerating(true);
    setGeneratedQuestions([]);
    setGenerationStatus(null);
    setJobId(null);
    setCanAddQuestions(false);
    setHasShownRegenerationSuccess(false);

    try {
      const filteredTags = formData.tags.filter(
        (tag) =>
          !formData.skill.some((s) => s.toLowerCase() === tag.toLowerCase()) &&
          tag.toLowerCase() !== formData.level.toLowerCase()
      );

      const res = await axios.post(
        `/api/integration/external-service/ai/generate/self-assessment/${selfAssessmentId}`,
        {
          ...formData,
          category: formData.category,
          studentId,
          tags: filteredTags.length > 0 ? filteredTags : formData.tags,
        }
      );
      const newJobId = res.data.result?.jobId || res.data.jobId;
      setJobId(newJobId);
      onAlert("AI question generation started!", "info");
    } catch (error: any) {
      setIsGenerating(false);
      onAlert(error.response?.data?.message || "Failed to generate questions", "error");
    }
  };

  const handleAddSingleQuestion = async (question: any, index: number) => {
    setIsAddingQuestions(true);
    try {
      await axios.post(
        `/api/integration/external-service/ai/add-single-to-self-assessment/${selfAssessmentId}/${jobId}/${index}`,
        { studentId }
      );
      onAlert("Question added successfully!", "success");
      onQuestionsAdded();
      setGeneratedQuestions((prev) => prev.filter((q) => q.id !== question.id));
      setOriginalQuestionCount((prev) => prev - 1);
      if (generatedQuestions.length === 1) {
        setTimeout(onClose, 800);
      }
    } catch (error: any) {
      onAlert(error.response?.data?.message || "Failed to add question", "error");
    } finally {
      setIsAddingQuestions(false);
    }
  };

  const handleAddAllQuestions = async () => {
    setIsAddingQuestions(true);
    try {
      await axios.post(
        `/api/integration/external-service/ai/add-to-self-assessment/${selfAssessmentId}/${jobId}`,
        { studentId }
      );
      onAlert(`All ${generatedQuestions.length} questions added!`, "success");
      onQuestionsAdded();
      // Clear all state before closing
      resetFormState();
      setTimeout(onClose, 800);
    } catch (error: any) {
      console.error("Error adding questions:", error.response?.data || error);
      onAlert(error.response?.data?.message || "Failed to add questions", "error");
    } finally {
      setIsAddingQuestions(false);
    }
  };

  const resetFormState = () => {
    setFormData({
      type: "",
      category: "",
      skill: [],
      numberOfQuestions: 1,
      level: "",
      tags: [],
      additionalContext: "",
    });
    setGeneratedQuestions([]);
    setJobId(null);
    setOriginalQuestionCount(0);
    setGenerationStatus(null);
    setRegenerationJobId(null);
    setIsRegenerating(false);
    setHasShownRegenerationSuccess(false);
    setCanAddQuestions(false);
    setTypeError(false);
    setCategoryError(false);
    setSkillError(false);
    setLevelError(false);
    setTagError(false);
    setDeletingQuestions(new Set());
    setNewSkillInput("");
    setNewTagInput("");
  };

  const handleDeleteQuestion = async (question: any, index: number) => {
    setDeletingQuestions((prev) => new Set(prev).add(question.id));
    try {
      await axios.delete(`/api/integration/external-service/ai/delete/${jobId}/${index}`);
      setGeneratedQuestions((prev) => prev.filter((q) => q.id !== question.id));
      onAlert("Question removed!", "success");
    } catch (error: any) {
      onAlert("Failed to remove question", "error");
    } finally {
      setDeletingQuestions((prev) => {
        const s = new Set(prev);
        s.delete(question.id);
        return s;
      });
    }
  };

  const handleGenerateMore = async () => {
    const deletedCount = originalQuestionCount - generatedQuestions.length;
    if (deletedCount <= 0) {
      onAlert("No questions were deleted", "warning");
      return;
    }

    setIsRegenerating(true);
    setCanAddQuestions(false);
    setHasShownRegenerationSuccess(false);

    try {
      const res = await axios.post(`/api/integration/external-service/ai/regenerate/${jobId}`, {
        numberOfQuestions: deletedCount,
      });
      setRegenerationJobId(res.data.jobId || res.data.result?.jobId);
      onAlert(`Regenerating ${deletedCount} question(s)...`, "info");
    } catch (error) {
      setIsRegenerating(false);
      setCanAddQuestions(true);
      onAlert("Failed to regenerate", "error");
    }
  };

  const showQuestions = generatedQuestions.length > 0 && !isGenerating;

  return (
    <>
      <Dialog open={addSkillDialogOpen} onClose={() => setAddSkillDialogOpen(false)} maxWidth="xs"
        PaperProps={{ sx: { position: "absolute", width: "350px" } }}
      >
        <DialogTitle sx={{ fontSize: "16px", py: 1.5 }}>
          Add New {selectedCategoryConfig.label}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Skill Name"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={newSkillInput}
            onChange={(e) => setNewSkillInput(e.target.value)}
            onKeyPress={(e) => { if (e.key === "Enter") handleAddNewSkillFromDialog(); }}
            placeholder={`Add ${selectedCategoryConfig.label.toLowerCase()}`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddSkillDialogOpen(false)} color="inherit" size="small">
            {t`Cancel`}
          </Button>
          <Button onClick={handleAddNewSkillFromDialog} variant="contained" color="primary" size="small" disabled={isAddingNewSkill}>
            {isAddingNewSkill ? "Adding..." : `Add ${selectedCategoryConfig.label}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addTagDialogOpen} onClose={() => setAddTagDialogOpen(false)} maxWidth="xs"
        PaperProps={{ sx: { position: "absolute", width: "350px" } }}
      >
        <DialogTitle sx={{ fontSize: "16px", py: 1.5 }}>Add New Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tag Name"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyPress={(e) => { if (e.key === "Enter") handleAddNewTagFromDialog(); }}
            placeholder="e.g., Frontend, Backend, Database"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddTagDialogOpen(false)} color="inherit" size="small">
            {t`Cancel`}
          </Button>
          <Button onClick={handleAddNewTagFromDialog} variant="contained" color="primary" size="small" disabled={isAddingNewTag}>
            {isAddingNewTag ? "Adding..." : "Add Tag"}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        transitionDuration={{ enter: 800, exit: 800 }}
      >
        <Box sx={{ width: { sm: "100vw", md: "40vw" } }}>
          <Box
            sx={{
              backgroundColor: "primary.dark",
              p: "5px 10px",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "33px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyIcon fontSize="small" />
              {t`Generate Questions with AI`}
            </Box>
            <IconButton
              onClick={handleCloseDrawer}
              sx={{ color: "grey.400", "&:hover": { color: "#f94449" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflow: "auto" }}>
            {!showQuestions && !isGenerating && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth error={typeError}>
                    <Select
                      value={formData.type}
                      onChange={(e) => handleInputChange("type", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        {t`Select Question Type *`}
                      </MenuItem>
                      {questionTypes.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {typeError && (
                      <Typography color="error" variant="caption">
                        {t`Required`}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth error={levelError}>
                    <Select
                      value={formData.level}
                      onChange={(e) => handleInputChange("level", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        {t`Select Level *`}
                      </MenuItem>
                      {levels.map((l) => (
                        <MenuItem key={l.value} value={l.value}>
                          {l.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {levelError && (
                      <Typography color="error" variant="caption">
                        {t`Required`}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl size="small" required fullWidth error={categoryError}>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={(e) => handleInputChange("category", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
                        Select Category *
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {categoryError && (
                      <Typography color="error" variant="caption">
                        Category is required.
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Autocomplete
                    multiple
                    options={availableSkills}
                    value={formData.skill}
                    disabled={!formData.category}
                    open={skillDropdownOpen}
                    onOpen={() => setSkillDropdownOpen(true)}
                    onClose={() => setSkillDropdownOpen(false)}
                    onChange={(e, v) => handleInputChange("skill", v)}
                    loading={skillsLoading}
                    disableCloseOnSelect
                    disableClearable
                    getOptionLabel={(option) => option}
                    noOptionsText={
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">No options</Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddSkillDialogOpen(true);
                            setSkillDropdownOpen(false);
                          }}
                          sx={{ textTransform: "none", fontSize: "12px" }}
                        >
                          Add New {selectedCategoryConfig.label}
                        </Button>
                      </Box>
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} label={option} {...tagProps} size="small" sx={{ fontSize: "12px" }} />;
                      })
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    ListboxComponent={(props) => {
                      const { children, ...other } = props;
                      return (
                        <div style={{ maxHeight: 300, display: "flex", flexDirection: "column" }}>
                          <ul
                            {...other}
                            ref={(node) => { if (node) node.scrollTop = skillScrollPositionRef.current; }}
                            onScroll={(e) => { skillScrollPositionRef.current = (e.currentTarget as HTMLUListElement).scrollTop; }}
                            style={{ margin: 0, padding: 0, listStyle: "none", overflow: "auto", flexGrow: 1 }}
                          >
                            {children}
                          </ul>
                          <div
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setAddSkillDialogOpen(true); }}
                            style={{
                              position: "sticky", bottom: 0,
                              backgroundColor: theme.palette.primary.dark, color: "#ffffff",
                              padding: "10px 16px", cursor: "pointer", borderTop: "1px solid #ccc",
                              display: "flex", alignItems: "center", flexShrink: 0,
                            }}
                          >
                            <AddIcon style={{ marginRight: 8 }} />
                            Add New {selectedCategoryConfig.label}
                          </div>
                        </div>
                      );
                    }}
                    renderInput={(params) => {
                      const customParams = {
                        ...params,
                        InputProps: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {skillDropdownOpen && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setSkillDropdownOpen(false); }}
                                  sx={{ position: "absolute", right: 30, padding: "4px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          ),
                        },
                      };
                      return (
                        <TextField
                          {...customParams}
                          name="skill"
                          variant="filled"
                          label={selectedCategoryConfig.label}
                          placeholder={
                            formData.skill?.length > 0 ? "" :
                            formData.category ? `Select ${selectedCategoryConfig.label.toLowerCase()}` : "Select category first"
                          }
                          required
                          error={skillError}
                          helperText={skillError ? `At least one ${selectedCategoryConfig.label.toLowerCase()} is required` : ""}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <TextField
                    label={t`Number of Questions`}
                    type="number"
                    fullWidth
                    value={formData.numberOfQuestions}
                    onChange={(e) => {
                      const value = e.target.value === "" ? "" : Number(e.target.value);
                      handleInputChange("numberOfQuestions", value);
                    }}
                    inputProps={{ min: 1, max: 25 }}
                    error={(formData.numberOfQuestions as number) < 1 || (formData.numberOfQuestions as number) > 25}
                    helperText={
                      (formData.numberOfQuestions as number) < 1
                        ? "Number of questions should be at least 1"
                        : (formData.numberOfQuestions as number) > 25
                        ? "Number of questions should not be more than 25"
                        : ""
                    }
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc !important" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#999 !important" },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555 !important" },
                      "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
                      "& input:focus": { outline: "none !important", boxShadow: "none !important" },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Autocomplete
                    multiple
                    options={availableTags}
                    value={formData.tags}
                    disabled={!formData.category}
                    open={tagDropdownOpen}
                    onOpen={() => setTagDropdownOpen(true)}
                    onClose={() => setTagDropdownOpen(false)}
                    onChange={(e, v) => handleInputChange("tags", v)}
                    loading={tagsLoading}
                    disableCloseOnSelect
                    disableClearable
                    getOptionLabel={(option) => option}
                    noOptionsText={
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">No options</Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddTagDialogOpen(true);
                            setTagDropdownOpen(false);
                          }}
                          sx={{ textTransform: "none", fontSize: "12px" }}
                        >
                          Add New Tag
                        </Button>
                      </Box>
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} label={option} {...tagProps} size="small" sx={{ fontSize: "12px" }} />;
                      })
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    ListboxComponent={(props) => {
                      const { children, ...other } = props;
                      return (
                        <div style={{ maxHeight: 300, display: "flex", flexDirection: "column" }}>
                          <ul
                            {...other}
                            ref={(node) => { if (node) node.scrollTop = skillScrollPositionRef.current; }}
                            onScroll={(e) => { skillScrollPositionRef.current = (e.currentTarget as HTMLUListElement).scrollTop; }}
                            style={{ margin: 0, padding: 0, listStyle: "none", overflow: "auto", flexGrow: 1 }}
                          >
                            {children}
                          </ul>
                          <div
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setAddTagDialogOpen(true); }}
                            style={{
                              position: "sticky", bottom: 0,
                              backgroundColor: theme.palette.primary.dark, color: "#ffffff",
                              padding: "10px 16px", cursor: "pointer", borderTop: "1px solid #ccc",
                              display: "flex", alignItems: "center", flexShrink: 0,
                            }}
                          >
                            <AddIcon style={{ marginRight: 8 }} />
                            Add New Tag
                          </div>
                        </div>
                      );
                    }}
                    renderInput={(params) => {
                      const customParams = {
                        ...params,
                        InputProps: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {tagDropdownOpen && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setTagDropdownOpen(false); }}
                                  sx={{ position: "absolute", right: 30, padding: "4px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          ),
                        },
                      };
                      return (
                        <TextField
                          {...customParams}
                          name="tags"
                          variant="filled"
                          label={t`Tags`}
                          placeholder={
                            formData.tags?.length > 0 ? "" :
                            formData.category ? "Select tags or add new tag" : "Select category first"
                          }
                          required
                          error={tagError}
                          helperText={tagError ? "At least one tag is required" : ""}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ my: 1 }}>
                  <TextField
                    label={t`Additional Context`}
                    multiline
                    rows={4}
                    fullWidth
                    value={formData.additionalContext}
                    onChange={(e) => handleInputChange("additionalContext", e.target.value)}
                    placeholder={t`Any specific requirements...`}
                    sx={{
                      '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderWidth: '1px' } },
                      '& textarea:focus': { boxShadow: 'none !important' },
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {isGenerating && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {t`Generating Questions...`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t`Creating`} {formData.numberOfQuestions} {formData.type} {t`questions`}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={generationStatus?.progress || 0}
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2">
                  {generationStatus?.progress || 0}% {t`Complete`}
                </Typography>
              </Box>
            )}

            {showQuestions && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" color="primary">
                    {t`Generated Questions`} ({generatedQuestions.length})
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={handleAddAllQuestions}
                      startIcon={<AddIcon />}
                      variant="contained"
                      color="success"
                      disabled={isAddingQuestions || !canAddQuestions}
                      sx={{ textTransform: "none" }}
                    >
                      {t`Add All`}
                    </Button>
                    <Button
                      size="small"
                      onClick={handleGenerateMore}
                      startIcon={<RefreshIcon />}
                      variant="outlined"
                      disabled={
                        isRegenerating ||
                        originalQuestionCount === generatedQuestions.length
                      }
                      sx={{ textTransform: "none" }}
                    >
                      {originalQuestionCount > generatedQuestions.length
                        ? `${t`Regenerate`} ${originalQuestionCount - generatedQuestions.length}`
                        : t`Regenerate`}
                    </Button>
                  </Box>
                </Box>

                {isRegenerating && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t`Regenerating`} {originalQuestionCount - generatedQuestions.length}{" "}
                    {t`question(s)...`}
                  </Alert>
                )}

                {!canAddQuestions && !isRegenerating && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t`Processing in progress`}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {generatedQuestions.map((q, i) => (
                    <Grid item xs={12} key={q.id}>
                      <Card sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={`${i + 1}. ${q.type}`}
                              size="small"
                              sx={{
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                py: 1,
                                '& .MuiChip-icon': {
                                  color: 'black', // green color
                                },
                              }}
                            />
                            <Chip icon={<BarChartIcon />} label={q.level} size="small" 
                            sx={{
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                py: 1,
                                '& .MuiChip-icon': {
                                  color: 'black', // green color
                                },
                              }}
                            />
                          </Box>
                          <Box display="flex" gap={1}>
                            {/* <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleAddSingleQuestion(q, i)}
                              disabled={isAddingQuestions || !canAddQuestions}
                              startIcon={<AddIcon />}
                            >
                              {t`Add`}
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteQuestion(q, i)}
                              disabled={deletingQuestions.has(q.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton> */}
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleAddSingleQuestion(q, i)}
                              disabled={isAddingQuestions || !canAddQuestions}
                              startIcon={<AddIcon fontSize="small" />}
                              sx={{
                                minWidth: 'auto',
                                px: 1,
                                fontSize: '0.7rem',
                                textTransform: "none",
                              }}
                            >
                              {`Add`}
                            </Button>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteQuestion(q, i)}
                              disabled={deletingQuestions.has(q.id)}
                            >
                              <DeleteIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: "14px", mb: 2 }}>
                          {q.question_text}
                        </Typography>
                        {/* {q.option_1 && (
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            {[q.option_1, q.option_2, q.option_3, q.option_4].map(
                              (opt, idx) =>
                                opt && (
                                  <Grid item xs={6} key={idx}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      {q.correct_answer.includes(idx + 1) ? (
                                        <CheckCircleIcon color="success" />
                                      ) : (
                                        <CloseIcon color="error" />
                                      )}
                                      <Typography variant="body2">{opt}</Typography>
                                    </Box>
                                  </Grid>
                                )
                            )}
                          </Grid>
                        )} */}
                        {/* <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>
                          {t`Answer explanation`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {q.explanation}
                        </Typography> */}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>

          {!showQuestions && !isGenerating && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 3 }}>
              <Button variant="contained" onClick={handleGenerateQuestions} sx={{ textTransform: "none" }}>
                {t`Generate Questions with AI`}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default AiQuestionGenerator;